<template>
  <div class="card mb-4 radius-7 advanced-initial-message">
    <div class="card-head bg-white p-15 p-sm-20 p-md-25 radius-7 d-flex flex-wrap justify-content-between align-items-start gap-3">
      <div>
        <h5 class="card-title mb-1"><i class="ri-sparkling-line me-2"></i>Mensagem inicial da IA avançada</h5>
        <p class="text-muted mb-0">Oriente quem inicia a conversa sem um pedido claro. Quem já sabe o que quer segue direto para o atendimento.</p>
      </div>
      <button v-if="configuration && !editing" type="button" class="btn btn-outline-primary btn-sm" @click="startEditing">Editar</button>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4" role="status">Carregando mensagem inicial...</div>
      <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
        <button v-if="!configuration" type="button" class="btn btn-sm btn-outline-danger ms-2" @click="load">Tentar novamente</button>
      </div>
      <template v-if="configuration">
        <div class="row g-4">
          <div class="col-lg-7">
            <fieldset :disabled="!editing || saving">
              <div class="form-check form-switch mb-3">
                <input id="advanced-welcome-enabled" v-model="draft.enabled" class="form-check-input" type="checkbox">
                <label class="form-check-label" for="advanced-welcome-enabled">Usar mensagem inicial quando o pedido não estiver claro</label>
              </div>
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <label class="form-label mb-0" for="advanced-welcome-body">Apresentação</label>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="insertName">Inserir {client_name}</button>
              </div>
              <textarea id="advanced-welcome-body" ref="bodyInput" v-model="draft.body_text" class="form-control" rows="9" maxlength="2000"></textarea>
              <p class="small text-muted mt-2">Use <code>{client_name}</code> se quiser incluir o primeiro nome. Se ele não for conhecido, o marcador será removido. Escreva as opções nos campos abaixo.</p>

              <div class="row g-3 mb-4">
                <div class="col-sm-6">
                  <label class="form-label" for="advanced-welcome-mode">Formato</label>
                  <select id="advanced-welcome-mode" v-model="draft.display_mode" class="form-select">
                    <option value="text">Texto numerado</option>
                    <option value="buttons">Botões de resposta (até 3)</option>
                    <option value="list">Lista de opções</option>
                  </select>
                </div>
                <div v-if="draft.display_mode === 'list'" class="col-sm-6">
                  <label class="form-label" for="advanced-welcome-list-button">Botão que abre a lista</label>
                  <input id="advanced-welcome-list-button" v-model="draft.list_button_text" class="form-control" maxlength="20">
                </div>
              </div>
              <div v-if="provider !== 'cloud_api'" class="alert alert-info small">O canal atual usa QR Code: a mensagem será enviada como texto numerado. Botões e listas ficam disponíveis ao usar a API Oficial.</div>
              <p v-else class="small text-muted">A API Oficial aceita até três botões. Para as quatro ações, escolha lista. As descrições aparecem no texto e na lista.</p>

              <div v-for="(option, index) in draft.options" :key="option.action" class="welcome-option mb-3">
                <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
                  <div class="form-check mb-0">
                    <input :id="`welcome-option-${option.action}`" v-model="option.enabled" class="form-check-input" type="checkbox">
                    <label class="form-check-label fw-bold" :for="`welcome-option-${option.action}`">{{ actionNames[option.action] }}</label>
                  </div>
                  <div class="d-flex gap-1">
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="index === 0" :aria-label="`Mover ${actionNames[option.action]} para cima`" @click="moveOption(index, -1)">↑</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="index === draft.options.length - 1" :aria-label="`Mover ${actionNames[option.action]} para baixo`" @click="moveOption(index, 1)">↓</button>
                  </div>
                </div>
                <div class="row g-2">
                  <div class="col-3 col-sm-2">
                    <label class="form-label small" :for="`welcome-emoji-${option.action}`">Emoji</label>
                    <input :id="`welcome-emoji-${option.action}`" v-model="option.emoji" class="form-control" maxlength="16" placeholder="—">
                  </div>
                  <div class="col-9 col-sm-10">
                    <label class="form-label small" :for="`welcome-title-${option.action}`">Título</label>
                    <input :id="`welcome-title-${option.action}`" v-model="option.title" class="form-control" maxlength="100">
                  </div>
                  <div class="col-12">
                    <label class="form-label small" :for="`welcome-description-${option.action}`">Descrição (opcional)</label>
                    <textarea :id="`welcome-description-${option.action}`" v-model="option.description" class="form-control" rows="2" maxlength="500"></textarea>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
          <div class="col-lg-5">
            <div class="welcome-preview">
              <h6><i class="ri-whatsapp-line me-2"></i>Pré-visualização</h6>
              <label class="small mb-1" for="welcome-preview-client">Cliente na prévia</label>
              <select id="welcome-preview-client" v-model="previewName" class="form-select form-select-sm mb-3">
                <option value="Andressa">Nome conhecido: Andressa</option>
                <option value="">Nome ainda desconhecido</option>
              </select>
              <p class="small text-muted">{{ formatLabel }}</p>
              <p v-if="!draft.enabled" class="alert alert-secondary small">A mensagem inicial está desativada.</p>
              <div class="welcome-bubble">
                <pre>{{ preview.mode === 'text' ? preview.text : preview.body }}</pre>
                <template v-if="preview.mode === 'buttons'">
                  <div v-for="option in preview.options" :key="option.action" class="native-choice">{{ optionTitle(option) }}</div>
                </template>
                <div v-if="preview.mode === 'list'" class="native-choice"><i class="ri-list-check me-1"></i>{{ draft.list_button_text }}</div>
              </div>
              <div v-if="preview.mode === 'list'" class="list-preview mt-3">
                <span class="small text-muted d-block mb-2">Ao tocar em “{{ draft.list_button_text }}”</span>
                <div v-for="option in preview.options" :key="option.action" class="list-choice">
                  <strong>{{ optionTitle(option) }}</strong><small v-if="option.description">{{ option.description }}</small>
                </div>
              </div>
              <p class="small text-muted mt-3 mb-0">Exibida uma vez por sessão, somente quando não houver um pedido identificável. A cliente pode escrever livremente a qualquer momento.</p>
            </div>
          </div>
        </div>
        <div v-if="editing" class="mt-4">
          <div v-if="validationError" class="alert alert-warning" role="alert">{{ validationError }}</div>
          <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-secondary" :disabled="saving" @click="cancel">Cancelar</button>
            <button type="button" class="btn btn-success" :disabled="saving || Boolean(validationError)" @click="save">{{ saving ? 'Salvando...' : 'Salvar configuração' }}</button>
          </div>
        </div>
        <div v-if="success" class="alert alert-success mt-4 mb-0" role="status">{{ success }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent, nextTick } from 'vue';
import api from '../../config/axios.js';
import { buildAdvancedPreview, optionTitle, validateAdvancedMessage } from '../../utils/advancedInitialMessage';

const clone = value => JSON.parse(JSON.stringify(value));
export default defineComponent({
  name: 'AdvancedInitialWhatsappMessageConfig',
  data: () => ({
    configuration: null, draft: null, editing: false, loading: false, saving: false, error: '', success: '', previewName: 'Andressa',
    actionNames: { schedule: 'Agendar', catalog: 'Catálogo', my_schedules: 'Meus agendamentos', handoff: 'Atendente' },
  }),
  computed: {
    provider() { return this.$store.getters.getWhatsappSettings?.provider || 'whatsapp_web_js'; },
    preview() { return buildAdvancedPreview(this.draft, this.previewName, this.provider); },
    validationError() { return this.draft ? validateAdvancedMessage(this.draft) : ''; },
    formatLabel() { return { text: 'Envio em texto numerado', buttons: 'Envio com botões de resposta', list: 'Envio com lista de opções' }[this.preview.mode]; },
  },
  methods: {
    optionTitle,
    async load() {
      this.loading = true; this.error = '';
      try { this.configuration = (await api.axios.get('/api/whatsapp/advanced-initial-message')).data; this.draft = clone(this.configuration); }
      catch (error) { this.error = error.response?.data?.message || 'Não foi possível carregar a mensagem inicial. Tente novamente.'; }
      finally { this.loading = false; }
    },
    startEditing() { this.draft = clone(this.configuration); this.editing = true; this.success = ''; this.error = ''; },
    cancel() { this.draft = clone(this.configuration); this.editing = false; this.error = ''; },
    moveOption(index, offset) { const options = [...this.draft.options]; [options[index], options[index + offset]] = [options[index + offset], options[index]]; this.draft.options = options; },
    async insertName() {
      const input = this.$refs.bodyInput;
      const start = input.selectionStart; const end = input.selectionEnd; const marker = '{client_name}';
      this.draft.body_text = this.draft.body_text.slice(0, start) + marker + this.draft.body_text.slice(end);
      await nextTick(); input.focus(); input.setSelectionRange(start + marker.length, start + marker.length);
    },
    async save() {
      if (this.validationError || this.saving) return;
      this.saving = true; this.error = ''; this.success = '';
      try {
        this.configuration = (await api.axios.put('/api/whatsapp/advanced-initial-message', this.draft)).data;
        this.draft = clone(this.configuration); this.editing = false;
        this.success = 'Configuração salva. Ela será usada nas próximas aberturas de sessão.';
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.error = (errors ? Object.values(errors).flat()[0] : null) || error.response?.data?.message || 'Não foi possível salvar. Seu texto foi preservado para tentar novamente.';
      } finally { this.saving = false; }
    },
  },
  mounted() { this.load(); },
});
</script>

<style scoped>
.advanced-initial-message { min-width: 0; overflow: hidden; }
fieldset { min-width: 0; }
textarea { line-height: 1.55; resize: vertical; }
.welcome-option { padding: 16px; border: 1px solid #e2e8f0; border-radius: 9px; }
.welcome-preview { background: #efeae2; border-radius: 10px; padding: 18px; position: sticky; top: 20px; }
.welcome-bubble { background: #fff; border-radius: 9px; padding: 15px; color: #17212b; overflow-wrap: anywhere; }
.welcome-bubble pre { white-space: pre-wrap; font: inherit; line-height: 1.6; margin: 0; }
.native-choice { text-align: center; color: #087e76; padding: 12px 4px 0; margin-top: 12px; border-top: 1px solid #e2e8f0; font-weight: 600; }
.list-preview { background: #fff; border-radius: 9px; padding: 15px; color: #17212b; }
.list-choice { padding: 10px 0; overflow-wrap: anywhere; }
.list-choice + .list-choice { border-top: 1px solid #e2e8f0; }
.list-choice small { display: block; margin-top: 4px; }
@media (max-width: 575px) { .welcome-option, .welcome-preview { padding: 12px; } }
</style>

<style>
body.dark .advanced-initial-message .welcome-preview { background: #111b21; }
body.dark .advanced-initial-message .welcome-bubble, body.dark .advanced-initial-message .list-preview { background: #202c33; color: #f8fafc; }
body.dark .advanced-initial-message .welcome-option, body.dark .advanced-initial-message .native-choice, body.dark .advanced-initial-message .list-choice { border-color: #334155; }
body.dark .advanced-initial-message .native-choice { color: #5bd5bd; }
</style>
